import { useState, useRef } from "react";
import { ChevronUp, ChevronDown, Trash2, Plus, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "../../lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;
export const inputCls = "w-full bg-transparent border border-white/20 focus:border-white outline-none text-white px-3 py-2 text-sm";
export const labelCls = "font-mono-x text-[10px] tracking-[0.2em] uppercase text-white/50 block mb-1.5";
export const imgSrc = (u) => (u && u.startsWith("/api/") ? `${API}${u}` : u);

export function ImageDrop({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const handleFiles = async (files) => {
    const file = files && files[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="relative w-full max-w-[240px] group">
        <img src={imgSrc(value)} alt="" className="w-full h-28 object-cover border border-white/15" />
        <button type="button" onClick={() => onChange("")} data-testid={testid ? `${testid}-remove` : undefined}
          className="absolute top-1.5 right-1.5 p-1 bg-black/70 text-white/80 hover:text-white" title="Remove image">
          <X size={13} />
        </button>
      </div>
    );
  }
  return (
    <div>
      <div
        data-testid={testid}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center gap-2 h-28 max-w-[240px] border border-dashed cursor-pointer transition-colors ${over ? "border-white bg-white/5" : "border-white/25 hover:border-white/60"}`}>
        <UploadCloud size={18} className="text-white/50" />
        <p className="text-[11px] text-white/50">{busy ? "Uploading…" : "Drop image or click"}</p>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
    </div>
  );
}

function TextField({ def, value, onChange, tid }) {
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={inputCls} />
    </div>
  );
}

function TextareaField({ def, value, onChange, tid }) {
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} rows={3} className={inputCls} />
    </div>
  );
}

function BoolField({ def, value, onChange, tid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} className="accent-[#5B7F9E] w-4 h-4" />
      <span className="text-sm text-white/70">{def.label}</span>
    </label>
  );
}

function SelectField({ def, value, onChange, tid }) {
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} className={`${inputCls} bg-[#050A15]`}>
        {def.options.map((o) => <option key={o} value={o}>{o === "" ? "default" : o}</option>)}
      </select>
    </div>
  );
}

function ImagesField({ def, value, onChange, tid }) {
  const list = value || [];
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      <div className="flex flex-wrap gap-3">
        {list.map((img, i) => (
          <div key={i} className="relative w-24 group">
            <img src={imgSrc(img)} alt="" className="w-24 h-16 object-cover border border-white/15" />
            <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))}
              className="absolute top-1 right-1 p-0.5 bg-black/70 text-white/80 hover:text-white"><X size={11} /></button>
          </div>
        ))}
        <div className="w-24">
          <ImageDrop value="" onChange={(url) => onChange([...list, url])} testid={`${tid}-add`} />
        </div>
      </div>
    </div>
  );
}

function LinesField({ def, value, onChange, tid }) {
  const list = value || [];
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      {list.map((line, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <textarea value={line} rows={line.length > 80 ? 3 : 1} data-testid={`${tid}-${i}`}
            onChange={(e) => onChange(list.map((l, x) => (x === i ? e.target.value : l)))} className={inputCls} />
          <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))}
            className="p-2 text-white/40 hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, ""])} data-testid={`${tid}-add`}
        className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase text-white/60 hover:text-white mt-1">
        <Plus size={12} /> Add line
      </button>
    </div>
  );
}

function ObjectField({ def, value, onChange, tid }) {
  const obj = value || {};
  return (
    <div className="border border-white/10 p-4">
      <p className={labelCls}>{def.label}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {def.fields.map((f) => (
          <FieldEditor key={f.k} def={f} prefix={tid} value={obj[f.k]}
            onChange={(v) => onChange({ ...obj, [f.k]: v })} />
        ))}
      </div>
    </div>
  );
}

function ItemsField({ def, value, onChange, tid }) {
  const list = value || [];
  const swap = (i, j) => {
    const v = [...list];
    const tmp = v[i]; v[i] = v[j]; v[j] = tmp;
    onChange(v);
  };
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      {list.map((item, i) => (
        <div key={i} className="border border-white/10 p-4 mb-3 relative">
          <div className="absolute top-2 right-2 flex gap-1">
            <button type="button" disabled={i === 0} onClick={() => swap(i, i - 1)}
              className="p-1 text-white/40 hover:text-white disabled:opacity-20"><ChevronUp size={14} /></button>
            <button type="button" disabled={i === list.length - 1} onClick={() => swap(i, i + 1)}
              className="p-1 text-white/40 hover:text-white disabled:opacity-20"><ChevronDown size={14} /></button>
            <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} data-testid={`${tid}-${i}-delete`}
              className="p-1 text-white/40 hover:text-red-400"><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-20">
            {def.fields.map((f) => (
              <FieldEditor key={f.k} def={f} prefix={`${tid}-${i}`} value={item[f.k]}
                onChange={(v) => onChange(list.map((it, x) => (x === i ? { ...it, [f.k]: v } : it)))} />
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, {}])} data-testid={`${tid}-add`}
        className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.15em] uppercase text-white/60 hover:text-white">
        <Plus size={12} /> Add item
      </button>
    </div>
  );
}

const FIELD_COMPONENTS = {
  text: TextField,
  textarea: TextareaField,
  bool: BoolField,
  select: SelectField,
  images: ImagesField,
  lines: LinesField,
  object: ObjectField,
  items: ItemsField,
};

function ImageField({ def, value, onChange, tid }) {
  return (
    <div>
      <label className={labelCls}>{def.label}</label>
      <ImageDrop value={value} onChange={onChange} testid={tid} />
    </div>
  );
}
FIELD_COMPONENTS.image = ImageField;

export function FieldEditor({ def, value, onChange, prefix }) {
  const Cmp = FIELD_COMPONENTS[def.t];
  if (!Cmp) return null;
  return <Cmp def={def} value={value} onChange={onChange} tid={`${prefix}-${def.k}`} />;
}
